import React, { useState, useEffect, useRef } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Briefcase, 
  Shield, 
  Camera, 
  Save, 
  Loader2, 
  AlertCircle 
} from 'lucide-react';
import { useAuth } from '../../../state/auth/useAuth';
import { useToast } from '../../../hooks/useToast';
import { getMyProfile, updateMyProfile, updateProfilePicture } from '../../../api/profile';

const ProfileManagement = () => {
  const { user: authUser } = useAuth(); // Fallback to auth context if needed
  const { showSuccess, showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    gender: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data } = await getMyProfile();
      if (data && data.success) {
        setProfile(data.data);
        populateForm(data.data);
      }
    } catch (error) {
      console.error(error);
      showError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (data) => {
    const personal = data.personalDetails || {};
    const contact = personal.emergencyContact || {};
    
    setFormData({
      name: data.name || '',
      dateOfBirth: personal.dateOfBirth ? new Date(personal.dateOfBirth).toISOString().split('T')[0] : '',
      gender: personal.gender || data.gender || '',
      mobile: data.mobile || '',
      address: personal.address || data.address || '',
      city: personal.city || data.city || '',
      state: personal.state || data.state || '',
      country: personal.country || data.country || '',
      postalCode: personal.postalCode || data.postalCode || '',
      emergencyContact: {
        name: contact.name || (data.emergencyContact?.name) || '',
        phone: contact.phone || (data.emergencyContact?.phone) || '',
        relationship: contact.relationship || (data.emergencyContact?.relationship) || ''
      }
    });
  };

  const handleInputChange = (e, section = null) => {
    const { name, value } = e.target;
    
    if (section === 'emergencyContact') {
      setFormData(prev => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [name]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError('File size should be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('profilePicture', file);
      
      const res = await updateProfilePicture(formData);
      if (res.data.success) {
        showSuccess('Profile picture updated');
        // Update local state to show new image immediately if possible, or refetch
        fetchProfile();
      }
    } catch (error) {
      showError('Failed to update profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Only include fields that have actual values to avoid validation errors on empty optional fields
      const payload = {};

      if (formData.name?.trim()) payload.name = formData.name.trim();
      if (formData.mobile?.trim()) payload.mobile = formData.mobile.trim();
      if (formData.gender) payload.gender = formData.gender;
      // Convert YYYY-MM-DD to full ISO datetime expected by the validator
      if (formData.dateOfBirth) payload.dateOfBirth = new Date(formData.dateOfBirth).toISOString();
      if (formData.address?.trim()) payload.address = formData.address.trim();
      if (formData.city?.trim()) payload.city = formData.city.trim();
      if (formData.state?.trim()) payload.state = formData.state.trim();
      if (formData.country?.trim()) payload.country = formData.country.trim();
      if (formData.postalCode?.trim()) payload.postalCode = formData.postalCode.trim();

      // Only include emergencyContact if at least one field is filled
      const ec = formData.emergencyContact;
      if (ec.name?.trim() || ec.phone?.trim() || ec.relationship?.trim()) {
        payload.emergencyContact = {};
        if (ec.name?.trim()) payload.emergencyContact.name = ec.name.trim();
        if (ec.phone?.trim()) payload.emergencyContact.phone = ec.phone.trim();
        if (ec.relationship?.trim()) payload.emergencyContact.relationship = ec.relationship.trim();
      }

      const res = await updateMyProfile(payload);
      if (res.data.success) {
        showSuccess('Profile updated successfully');
        setProfile(res.data.data);
      }
    } catch (error) {
      showError(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-theme-border" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-10">
      {/* Header / Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
        <div className="h-32 bg-gradient-to-r from-[#2D869C] to-[#82C3D3]"></div>
        <div className="px-8 pb-8 flex flex-col md:flex-row items-center md:items-end -mt-16 gap-6">
          <div className="relative group">
            <div className="w-32 h-32 rounded-full border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center">
              {profile?.profilePicture?.url ? (
                <img 
                  src={profile.profilePicture.url} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <User size={48} className="text-gray-300" />
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-1 right-1 p-2 bg-theme-border text-white rounded-full shadow-lg hover:bg-[#256e80] transition-colors"
              title="Change Profile Picture"
            >
              <Camera size={16} />
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
          
          <div className="flex-1 text-center md:text-left mb-2">
            <h1 className="text-2xl font-bold text-gray-800">{profile?.name}</h1>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-2 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Mail size={16} /> {profile?.email}
              </span>
              <span className="flex items-center gap-1">
                <Briefcase size={16} /> {profile?.role}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${profile?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {profile?.status?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Read-only Professional Info */}
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Shield size={18} className="text-theme-border" />
                    Professional Details
                </h3>
                <div className="space-y-4">
                  {/* Since this is for receptionists, they might not have much here, but we show what exists */}
                  <div>
                      <label className="text-xs text-gray-500 font-medium uppercase">Role</label>
                      <p className="text-sm font-medium text-gray-800">{profile?.role || '-'}</p>
                  </div>
                  <div>
                      <label className="text-xs text-gray-500 font-medium uppercase">ID</label>
                      <p className="text-sm font-medium text-gray-800 break-all">{profile?.id}</p>
                  </div>
                  {profile?.professionalDetails && (
                    <>
                         {profile.professionalDetails.designation && (
                            <div>
                                <label className="text-xs text-gray-500 font-medium uppercase">Designation</label>
                                <p className="text-sm font-medium text-gray-800">{profile.professionalDetails.designation}</p>
                            </div>
                         )}
                         {profile.professionalDetails.department && (
                            <div>
                                <label className="text-xs text-gray-500 font-medium uppercase">Department</label>
                                <p className="text-sm font-medium text-gray-800">{profile.professionalDetails.department}</p>
                            </div>
                         )}
                         {profile.professionalDetails.joiningDate && (
                            <div>
                                <label className="text-xs text-gray-500 font-medium uppercase">Joined</label>
                                <p className="text-sm font-medium text-gray-800">{new Date(profile.professionalDetails.joiningDate).toLocaleDateString()}</p>
                            </div>
                         )}
                    </>
                  )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <AlertCircle size={18} className="text-theme-border" />
                    Account Info
                </h3>
                <div className="space-y-4">
                  <div>
                      <label className="text-xs text-gray-500 font-medium uppercase">Email</label>
                      <p className="text-sm font-medium text-gray-800">{profile?.email}</p>
                  </div>
                  <div>
                      <label className="text-xs text-gray-500 font-medium uppercase">Member Since</label>
                      <p className="text-sm font-medium text-gray-800">{profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
            </div>
        </div>

        {/* Right Column: Editable Personal Info */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <User size={18} className="text-theme-border" />
                    Personal Details
                </h3>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-theme-border text-white rounded-lg hover:bg-[#256e80] transition-colors disabled:opacity-70 text-sm font-medium"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Changes
                </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="form-group md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                    />
                </div>
                <div className="form-group">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                    />
                </div>
                <div className="form-group">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                    >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div className="form-group">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                    <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                    />
                </div>
            </div>

            <div className="border-t border-gray-100 my-6 pt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 uppercase tracking-wide">Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                            placeholder="Plot No., Colony, Street"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                        <input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                        />
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-100 my-6 pt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-4 uppercase tracking-wide">Emergency Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.emergencyContact.name}
                            onChange={(e) => handleInputChange(e, 'emergencyContact')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.emergencyContact.phone}
                            onChange={(e) => handleInputChange(e, 'emergencyContact')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                        <input
                            type="text"
                            name="relationship"
                            value={formData.emergencyContact.relationship}
                            onChange={(e) => handleInputChange(e, 'emergencyContact')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-border/20 focus:border-theme-border outline-none transition-all"
                            placeholder="e.g. Spouse, Parent"
                        />
                    </div>
                </div>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;
